import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, Check, Eye, Pin, PinOff, Plus, Trash2, X } from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { fmtDateTime } from "@/lib/format";

const useInvalidate = () => {
  const qc = useQueryClient();
  return () => ["admin-blog", "blog-settings", "blog-posts", "blog-post", "blog-comments"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
};

function Settings() {
  const refresh = useInvalidate();
  const { data } = useQuery({ queryKey: ["admin-blog", "settings"], queryFn: async () => (await api.get("/admin/blog/settings")).data });
  const [cats, setCats] = useState(null);
  const [draft, setDraft] = useState("");
  useEffect(() => { if (data) setCats(data.categories); }, [data]);
  if (!data || !cats) return <div className="h-32 animate-pulse rounded-2xl bg-muted" />;
  const save = async (patch) => {
    try { await api.put("/admin/blog/settings", { enabled: data.enabled, categories: cats, ...patch }); toast.success("Blog settings saved."); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <section className="rounded-2xl border border-border bg-card p-5" data-testid="blog-settings">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div><h2 className="font-serif text-lg font-semibold text-foreground">Blog visibility</h2><p className="text-sm text-muted-foreground">{data.enabled ? "The blog is live for visitors." : "The blog is hidden from visitors (admins can still preview it)."}</p></div>
        <label className="flex items-center gap-3 text-sm font-semibold"><span>{data.enabled ? "On" : "Off"}</span><Switch checked={data.enabled} onCheckedChange={(v) => save({ enabled: v })} data-testid="blog-enabled-switch" /></label>
      </div>
      <div className="mt-6">
        <h3 className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Categories</h3>
        <ul className="mt-2 flex flex-wrap gap-2" data-testid="blog-category-list">
          {cats.map((c) => <li key={c} className="flex items-center gap-1 rounded-full border border-border px-3 py-1 text-sm">{c}<button type="button" onClick={() => setCats(cats.filter((x) => x !== c))} aria-label={`Remove ${c}`} className="ml-1 text-muted-foreground hover:text-destructive" data-testid={`remove-category-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}><X className="h-3.5 w-3.5" /></button></li>)}
        </ul>
        <div className="mt-3 flex gap-2">
          <Input value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={80} placeholder="New category" className="h-9 max-w-xs rounded-full" data-testid="new-category-input" onKeyDown={(e) => { if (e.key === "Enter" && draft.trim()) { setCats([...cats, draft.trim()]); setDraft(""); } }} />
          <Button variant="outline" size="sm" className="rounded-full" disabled={!draft.trim()} onClick={() => { setCats([...cats, draft.trim()]); setDraft(""); }} data-testid="add-category"><Plus className="mr-1 h-4 w-4" /> Add</Button>
          <Button size="sm" className="rounded-full" onClick={() => save({ categories: cats })} data-testid="save-categories">Save categories</Button>
        </div>
      </div>
    </section>
  );
}

function Posts() {
  const refresh = useInvalidate();
  const [status, setStatus] = useState("pending");
  const [note, setNote] = useState({});
  const { data } = useQuery({ queryKey: ["admin-blog", "posts", status], queryFn: async () => (await api.get("/admin/blog/posts", { params: { status } })).data });
  const act = async (id, s, n) => {
    try { await api.put(`/admin/blog/posts/${id}/status`, { status: s, note: n || "" }); toast.success(s === "published" ? "Post published." : s === "rejected" ? "Post returned to the author." : `Post set to ${s}.`); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const feature = async (id, on) => {
    try { await api.put(`/admin/blog/posts/${id}/feature`, null, { params: { featured: on } }); toast.success(on ? "Pinned to the top of the blog." : "Unpinned."); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this post permanently?")) return;
    try { await api.delete(`/admin/blog/posts/${id}`); toast.success("Post deleted."); refresh(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const counts = data?.counts || {};
  return (
    <section className="rounded-2xl border border-border bg-card" data-testid="blog-moderation">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
        <h2 className="font-serif text-lg font-semibold text-foreground">Posts</h2>
        <div className="flex flex-wrap gap-1">
          {["pending", "published", "rejected", "draft"].map((s) => <button key={s} type="button" onClick={() => setStatus(s)} className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-wider ${status === s ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground hover:bg-muted"}`} data-testid={`blog-filter-${s}`}>{s === "pending" ? "awaiting review" : s} {counts[s] ?? ""}</button>)}
        </div>
      </header>
      {!data ? <div className="h-32 animate-pulse" /> : !data.posts.length ? <p className="p-8 text-center text-sm text-muted-foreground" data-testid="blog-moderation-empty">Nothing here.</p> : (
        <ul className="divide-y divide-border">
          {data.posts.map((p) => (
            <li key={p.id} className="grid gap-4 px-5 py-4 lg:grid-cols-[1fr_auto]" data-testid={`admin-post-${p.id}`}>
              <div className="flex gap-4">
                {p.cover_image_id && <img src={`${API}/blog/images/${p.cover_image_id}`} alt="" className="h-20 w-28 shrink-0 rounded-xl object-cover" />}
                <div className="min-w-0">
                  <p className="font-mono text-[10px] uppercase tracking-wider text-gold">{p.category}{p.featured && <span className="ml-2 rounded-full bg-gold/15 px-2 py-0.5 text-gold" data-testid={`featured-badge-${p.id}`}>Pinned</span>}</p>
                  <h3 className="font-serif text-lg font-semibold leading-tight text-foreground">{p.title}</h3>
                  <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{p.excerpt}</p>
                  <p className="mt-2 text-xs text-muted-foreground">{p.author.name} · {p.author.email} · updated {fmtDateTime(p.updated_at || p.created_at)}</p>
                  {p.flagged_images.length > 0 && <p className="mt-2 flex items-center gap-1.5 rounded-lg bg-red-50 px-2 py-1 text-xs text-red-800" data-testid={`flagged-${p.id}`}><AlertTriangle className="h-3.5 w-3.5" /> {p.flagged_images.length} photo{p.flagged_images.length > 1 ? "s" : ""} flagged by AI screening: {p.flagged_images.map((f) => f.reason).join("; ")}</p>}
                  {p.rejection_note && <p className="mt-2 text-xs text-muted-foreground">Note sent: {p.rejection_note}</p>}
                </div>
              </div>
              <div className="flex flex-col gap-2 lg:w-72">
                <Button asChild variant="outline" size="sm" className="rounded-full"><Link to={`/blog/${p.slug}`} target="_blank" data-testid={`admin-post-view-${p.id}`}><Eye className="mr-1.5 h-4 w-4" /> Read full post</Link></Button>
                {p.status !== "published" && <Button size="sm" className="rounded-full" onClick={() => act(p.id, "published")} data-testid={`admin-post-approve-${p.id}`}><Check className="mr-1.5 h-4 w-4" /> Approve &amp; publish</Button>}
                {p.status === "published" && <Button variant={p.featured ? "secondary" : "outline"} size="sm" className="rounded-full" onClick={() => feature(p.id, !p.featured)} data-testid={`admin-post-feature-${p.id}`}>{p.featured ? <PinOff className="mr-1.5 h-4 w-4" /> : <Pin className="mr-1.5 h-4 w-4" />} {p.featured ? "Unpin" : "Pin to top"}</Button>}
                {p.status !== "rejected" && (
                  <>
                    <Textarea value={note[p.id] || ""} onChange={(e) => setNote({ ...note, [p.id]: e.target.value })} rows={2} placeholder="Note to the author (optional)" className="text-xs" data-testid={`admin-post-note-${p.id}`} />
                    <Button variant="outline" size="sm" className="rounded-full" onClick={() => act(p.id, "rejected", note[p.id])} data-testid={`admin-post-reject-${p.id}`}><X className="mr-1.5 h-4 w-4" /> {p.status === "published" ? "Unpublish & return" : "Return to author"}</Button>
                  </>
                )}
                <Button variant="ghost" size="sm" className="rounded-full text-destructive hover:text-destructive" onClick={() => remove(p.id)} data-testid={`admin-post-delete-${p.id}`}><Trash2 className="mr-1.5 h-4 w-4" /> Delete</Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Comments() {
  const refresh = useInvalidate();
  const { data } = useQuery({ queryKey: ["admin-blog", "comments"], queryFn: async () => (await api.get("/admin/blog/comments", { params: { status: "pending" } })).data });
  const act = async (id, status) => {
    try { await api.put(`/admin/blog/comments/${id}/status`, { status }); refresh(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const rows = data?.comments || [];
  return (
    <section className="rounded-2xl border border-border bg-card" data-testid="blog-comment-moderation">
      <header className="border-b border-border px-5 py-4"><h2 className="font-serif text-lg font-semibold text-foreground">Comments awaiting review <span className="font-mono text-sm text-muted-foreground">({rows.length})</span></h2></header>
      {!rows.length ? <p className="p-6 text-center text-sm text-muted-foreground" data-testid="blog-comments-empty">No comments waiting.</p> : (
        <ul className="divide-y divide-border">
          {rows.map((c) => (
            <li key={c.id} className="flex flex-wrap items-start justify-between gap-3 px-5 py-3" data-testid={`admin-comment-${c.id}`}>
              <div className="min-w-0 flex-1"><p className="text-xs text-muted-foreground">{c.author.name} · {c.author.email} on <span className="text-foreground">{c.post?.title}</span></p><p className="mt-1 text-sm text-foreground">{c.text}</p></div>
              <div className="flex gap-2"><Button size="sm" className="rounded-full" onClick={() => act(c.id, "published")} data-testid={`admin-comment-approve-${c.id}`}><Check className="h-4 w-4" /></Button><Button size="sm" variant="outline" className="rounded-full" onClick={() => act(c.id, "rejected")} data-testid={`admin-comment-reject-${c.id}`}><X className="h-4 w-4" /></Button></div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default function AdminBlog() {
  return (
    <div className="space-y-6" data-testid="admin-blog">
      <Settings />
      <Posts />
      <Comments />
    </div>
  );
}
